import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useTranslations } from "@danishagro/shared/src/contexts/translations/translations.context";
import { formatNumber } from "@danishagro/shared/src/helpers/formatNumber.helper";
import { useAppData } from "@danishagro/shared/src/contexts/appData.context";
import { DA_ButtonLink } from "@danishagro/shared/src/components/atoms/ButtonLink/ButtonLink.component";
import { DA_IconNames } from "@danishagro/shared/src/components/atoms/Icon/Icon.component";
import { DA_Tooltip } from "@danishagro/shared/src/components/atoms/Tooltip/Tooltip.component";
import { cn } from "@danishagro/shared/src/helpers/classNames.helper";
import { useScreen } from "@danishagro/shared/src/contexts/screen.context";
import S from "./DiscountTableDesktop.module.scss";

interface DiscountRow {
    quantityFrom: number;
    quantityTo?: number;
    price: number;
}

interface DiscountTableDesktopProps {
    discountTable: DiscountRow[];
    currentQuantity: number;
    onRowClick?: (quantity: number) => void;
    clickableRowsDesktop?: boolean;
    priceCurrency?: string;
    minimumSalesQuantity?: string;
}

export const DiscountTableDesktop = ({
    discountTable,
    currentQuantity,
    onRowClick,
    clickableRowsDesktop,
    priceCurrency,
    minimumSalesQuantity,
}: DiscountTableDesktopProps) => {
    const { getDictionaryString } = useTranslations();
    const { currentCulture } = useAppData(); // If you need the currentCulture from your context
    const { isMobileOrTablet } = useScreen();

    const [isExpanded, setIsExpanded] = useState(false);

    const minQtyValue = parseInt(minimumSalesQuantity) || 1;

    // Find the active row (the row that matches `currentQuantity`)
    const getActiveRowIndex = () => {
        // Prefer exact match on quantityFrom — handles overlapping boundaries
        // where quantityTo of one row equals quantityFrom of the next
        const exactMatch = discountTable.findIndex((item) => currentQuantity === item.quantityFrom);
        if (exactMatch !== -1) return exactMatch;

        return discountTable.findIndex(
            (item) =>
                currentQuantity >= item.quantityFrom &&
                currentQuantity <= (item.quantityTo || Infinity)
        );
    };

    const activeRowIndex = getActiveRowIndex();

    useEffect(() => {
        // If the user’s activeRowIndex is 2 or more, show all rows
        if (activeRowIndex >= 2 && !isExpanded) {
            setIsExpanded(true);
        }
    }, [activeRowIndex, isExpanded]);

    const schemaMarkup = {
        "@context": "https://schema.org",
        "@type": "AggregateOffer",
        offers: discountTable.map((item) => ({
            "@type": "Offer",
            price: formatNumber(item.price, currentCulture, { decimals: 2 }),
            priceCurrency: priceCurrency,
            eligibleQuantity: {
                "@type": "QuantitativeValue",
                value: item.quantityFrom,
            },
        })),
    };

    // Decide how many rows to show
    const rowsToShow = isExpanded ? discountTable : discountTable.slice(0, 3);

    return (
        <div className={S.tableContainer}>
            <Helmet>
                <script type="application/ld+json">{JSON.stringify(schemaMarkup)}</script>
            </Helmet>
            <table className={S.discountTable}>
                <thead>
                    <tr>
                        <th className={S.titleLeft}>
                            {getDictionaryString("amountDiscount.amountDiscount")}
                        </th>
                        <th className={S.titleRight}>
                            {getDictionaryString("amountDiscount.pricePerPcs")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {rowsToShow.map((item, index) => {
                        const isBelowMin = item.quantityFrom < minQtyValue;
                        return (
                            <tr
                                key={index}
                                className={cn(
                                    index === activeRowIndex && S.activeRow,
                                    isBelowMin && S.disabledRow
                                )}
                                onClick={() => {
                                    if (
                                        clickableRowsDesktop &&
                                        !isMobileOrTablet &&
                                        onRowClick &&
                                        !isBelowMin
                                    ) {
                                        onRowClick(item.quantityFrom);
                                    }
                                }}
                            >
                                <td className={S.firstColumn}>
                                    {isBelowMin ? (
                                        <DA_Tooltip
                                            placement="top"
                                            text={getDictionaryString(
                                                "minimumQuantityInfoTextTooltip",
                                                { minQty: minQtyValue }
                                            )}
                                        >
                                            <span className={S.disabledRowContent}>
                                                {item.quantityFrom} {getDictionaryString("pcs")}
                                            </span>
                                        </DA_Tooltip>
                                    ) : (
                                        <>
                                            {item.quantityFrom} {getDictionaryString("pcs")}
                                        </>
                                    )}
                                </td>
                                <td className={cn(S.alignRight, S.secondColumn)}>
                                    {formatNumber(item.price, currentCulture, { decimals: 2 })}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>

            {/* Expand button if more than 3 discount rows exist */}
            {!isExpanded && discountTable.length > 3 && (
                <DA_ButtonLink
                    title={getDictionaryString("amountDiscount.showMore")}
                    className={S.expandButton}
                    onClick={() => setIsExpanded(true)}
                    icon={DA_IconNames.ChevronSmallDown}
                />
            )}
        </div>
    );
};
